import React from "react";
import SectionContainer from "../../components/SectionContainer";
import Reveal from "../../components/Reveal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faStar,
  faCogs,
  faBolt,
  faUsers,
  faShieldAlt,
  faSyncAlt,
  faTrophy,
  faQuoteLeft,
  faCheckCircle,
  faArrowRight,
  faAward,
} from "@fortawesome/free-solid-svg-icons";

const iconMap = {
  Innovation: faBolt,
  Expertise: faUsers,
  Quality: faShieldAlt,
  Customization: faCogs,
  Agility: faSyncAlt,
  Excellence: faStar,
};

const colorMap = {
  Innovation: "from-yellow-500 to-orange-500",
  Expertise: "from-blue-500 to-indigo-600",
  Quality: "from-green-500 to-emerald-600",
  Customization: "from-purple-500 to-violet-600",
  Agility: "from-cyan-500 to-blue-500",
  Excellence: "from-pink-500 to-rose-600",
};

const reasons = [
  {
    title: "Customized Solutions",
    description:
      "Software and data solutions tailored precisely to your business objectives with personalized approaches.",
    icon: "Customization",
    benefits: [
      "Tailored Architecture",
      "Custom Workflows",
      "Brand Integration",
    ],
    metric: "100% Custom",
  },
  {
    title: "Cross-Domain Expertise",
    description:
      "Experience across industries gives us deep insight into diverse challenges and proven solutions.",
    icon: "Expertise",
    benefits: ["15+ Industries", "500+ Projects", "Global Experience"],
    metric: "15+ Years",
  },
  {
    title: "Cutting-Edge Innovation",
    description:
      "We adopt modern architectures & emerging tech to future-proof outcomes and competitive advantage.",
    icon: "Innovation",
    benefits: ["Latest Technologies", "AI Integration", "Cloud Native"],
    metric: "99% Uptime",
  },
  {
    title: "Client-Centric Approach",
    description:
      "Collaborative partnership ensuring alignment with evolving priorities and business goals.",
    icon: "Excellence",
    benefits: ["24/7 Support", "Dedicated Teams", "Regular Updates"],
    metric: "4.9★ Rating",
  },
  {
    title: "Commitment to Quality",
    description:
      "Rigorous testing and governance for reliability, security, and scalability at every level.",
    icon: "Quality",
    benefits: ["ISO Certified", "Security First", "Performance Tested"],
    metric: "Zero Defects",
  },
  {
    title: "Agile Delivery",
    description:
      "Iterative releases accelerate value while adapting to ongoing feedback and changing requirements.",
    icon: "Agility",
    benefits: ["Fast Delivery", "Flexible Process", "Quick Iterations"],
    metric: "2x Faster",
  },
];

const testimonials = [
  {
    quote:
      "GeniusNexa delivered beyond our expectations with innovative solutions that transformed our business.",
    author: "Sarah Johnson",
    position: "CTO, TechCorp",
    rating: 5,
  },
  {
    quote:
      "The quality and attention to detail in their work is exceptional. Highly recommended!",
    author: "Michael Chen",
    position: "CEO, InnovateLab",
    rating: 5,
  },
];

const WhyChooseUsSection = () => {
  return (
    <SectionContainer id="whychooseus" variant="gradient">
      <div className="relative">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5 overflow-hidden">
          <svg className="w-full h-full" viewBox="0 0 100 100">
            <defs>
              <pattern
                id="why-choose-pattern"
                width="20"
                height="20"
                patternUnits="userSpaceOnUse"
              >
                <polygon
                  points="10,1 4,7 10,13 16,7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="0.5"
                />
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#why-choose-pattern)" />
          </svg>
        </div>

        {/* Header Section */}
        <div className="text-center mb-20 relative z-10">
          <div className="inline-flex items-center px-6 py-3 bg-[#E5C558]/10 rounded-full text-[#E5C558] font-semibold mb-6 border border-[#E5C558]/20">
            <FontAwesomeIcon icon={faTrophy} className="mr-2" />
            Industry Leaders
          </div>
          <h2 className="text-4xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-8 leading-tight">
            Why{" "}
            <span className="bg-gradient-to-r from-[#E5C558] to-[#d4b54d] bg-clip-text text-transparent">
              Choose Us
            </span>
          </h2>
          <p className="text-xl lg:text-2xl text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
            We're not just another tech company - we're your dedicated partner
            in growth, bringing fresh energy and personalized service to every
            project.
          </p>
        </div>

        {/* Reasons Grid */}
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3 mb-20 relative z-10">
          {reasons.map((reason, index) => (
            <Reveal key={index} delay={index * 0.1}>
              <div className="group relative bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 dark:border-gray-700 overflow-hidden hover:-translate-y-2">
                {/* Background Gradient */}
                <div
                  className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${
                    colorMap[reason.icon]
                  } opacity-10 rounded-full transform translate-x-16 -translate-y-16 group-hover:opacity-20 transition-opacity duration-500`}
                ></div>

                {/* Metric Badge */}
                <div className="absolute top-6 right-6">
                  <div
                    className={`px-3 py-1 rounded-full bg-gradient-to-r ${
                      colorMap[reason.icon]
                    } text-white text-xs font-bold shadow-md`}
                  >
                    {reason.metric}
                  </div>
                </div>

                {/* Icon */}
                <div
                  className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${
                    colorMap[reason.icon]
                  } flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}
                >
                  <FontAwesomeIcon
                    icon={iconMap[reason.icon] || faStar}
                    className="text-white text-2xl"
                  />
                </div>

                {/* Content */}
                <div className="relative z-10">
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 group-hover:text-[#E5C558] transition-colors duration-300">
                    {reason.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                    {reason.description}
                  </p>

                  {/* Benefits List */}
                  <div className="space-y-2 mb-6">
                    {reason.benefits.map((benefit, idx) => (
                      <div
                        key={idx}
                        className="flex items-center text-sm text-gray-600 dark:text-gray-400"
                      >
                        <FontAwesomeIcon
                          icon={faCheckCircle}
                          className="text-green-500 mr-3 flex-shrink-0"
                        />
                        {benefit}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Hover Effect Border */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Testimonials Section */}
        <div className="relative z-10 mb-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              What Our Clients Say
            </h3>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              Don't just take our word for it - hear from our satisfied clients
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => (
              <Reveal key={index} delay={0.2}>
                <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 shadow-lg border border-gray-100 dark:border-gray-700 relative">
                  <FontAwesomeIcon
                    icon={faQuoteLeft}
                    className="text-[#E5C558] text-3xl mb-4 opacity-50"
                  />
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed mb-6 text-lg italic">
                    "{testimonial.quote}"
                  </p>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-bold text-gray-900 dark:text-white">
                        {testimonial.author}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {testimonial.position}
                      </div>
                    </div>
                    <div className="flex text-[#E5C558]">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <FontAwesomeIcon
                          key={i}
                          icon={faStar}
                          className="text-sm"
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center relative z-10">
          <div className="bg-gradient-to-r from-[#E5C558]/10 to-[#d4b54d]/10 rounded-3xl p-12 border border-[#E5C558]/20 backdrop-blur-sm">
            <div className="flex justify-center mb-6">
              <FontAwesomeIcon
                icon={faAward}
                className="text-6xl text-[#E5C558]"
              />
            </div>
            <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Let's Build Something Great Together
            </h3>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
              We're excited to be part of your growth journey. Free consultation
              to understand your needs and explore how we can help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact-us"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] text-white font-bold rounded-full hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                Get Started Today
                <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
              </a>
              <a
                href="/portfolio"
                className="inline-flex items-center px-8 py-4 bg-transparent border-2 border-[#E5C558] text-[#E5C558] font-bold rounded-full hover:bg-[#E5C558] hover:text-white transition-all duration-300"
              >
                View Our Work
              </a>
            </div>
          </div>
        </div>
      </div>
    </SectionContainer>
  );
};

export default WhyChooseUsSection;
